/**
 * CART NIT VALIDATION — Fabletics GT (Essence theme)
 *
 * Validates the "nit" cart attribute before checkout.
 *
 * Rules:
 *   - Cart total >= threshold  → a valid NIT is required (CF not allowed)
 *   - Cart total <  threshold  → valid NIT or "CF"; if empty, ask via dialog
 *
 * Uses document-level capture delegation so it survives the cart modal
 * re-renders done through the Section Rendering API (cart-form component
 * replaces the modal HTML on every quantity change / add-to-cart).
 *
 * On a valid NIT the original submit is re-dispatched with requestSubmit()
 * instead of navigating to a hardcoded URL, so the theme's own handlers and
 * any third-party app listening on the checkout form still run, and the form
 * keeps its real action (locale/market prefixes included).
 */

(function () {
  'use strict';

  /* Formato NIT Guatemala: dígitos con guiones opcionales como separadores y
     un dígito verificador final que puede ser numérico o "K".
     Los guiones solo son válidos entre dígitos: rechaza "-", "--", "-1-", "-K".
     NO valida el dígito verificador (módulo 11) del SAT — ver README del feature. */
  var NIT_FORMAT = /^\d+(?:-\d+)*(?:-?K)?$/;
  var NIT_MIN_DIGITS = 3;
  var NIT_MAX_DIGITS = 15;

  var CF_VALUE = 'CF';
  var ATTRIBUTE_NAME = 'nit';
  /* Key used before the rename. Kept only to clear it from open carts. */
  var LEGACY_ATTRIBUTE_NAME = 'Número de NIT del cliente';
  var FIELD_SELECTOR = '.cart-nit-field input.cart-nit-field__input';

  /* Forms cuyo submit ya pasó la validación: dejamos correr el segundo submit
     para que lo procesen el tema y las apps con normalidad. */
  var validatedForms = new WeakSet();

  function cartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  }

  function normalize(value) {
    return String(value == null ? '' : value).trim().toUpperCase();
  }

  /**
   * @param {string} value - ya normalizado (trim + uppercase)
   * @returns {boolean}
   */
  function isValidNit(value) {
    if (!NIT_FORMAT.test(value)) return false;
    var digits = value.replace(/-/g, '').length;
    return digits >= NIT_MIN_DIGITS && digits <= NIT_MAX_DIGITS;
  }

  /**
   * Find the NIT field wrapper related to the submitted checkout form.
   * On the cart page the input lives inside the form itself; in the cart
   * modal the checkout form only wraps the button, so we search the
   * surrounding <cart-form> / <cart-modal> scope.
   * @param {HTMLFormElement} form
   * @returns {HTMLElement|null} the .cart-nit-field wrapper or null
   */
  function findNitWrapper(form) {
    var input = form.querySelector(FIELD_SELECTOR);
    if (input) return input.closest('.cart-nit-field');

    var scope = form.closest('cart-modal') || form.closest('cart-form') || form.closest('.shopify-section');
    if (scope) {
      input = scope.querySelector(FIELD_SELECTOR);
      if (input) return input.closest('.cart-nit-field');
    }
    return null;
  }

  /** parseInt que no confunde 0 con "sin valor" */
  function intOr(raw, fallback) {
    var parsed = parseInt(raw, 10);
    return isNaN(parsed) ? fallback : parsed;
  }

  /**
   * Read config from the field wrapper's data attributes.
   * @param {HTMLElement} wrapper
   */
  function getConfig(wrapper) {
    var d = wrapper.dataset;
    return {
      thresholdCents: intOr(d.nitThreshold, 250000),
      subtotalCents: intOr(d.cartSubtotal, 0),
      errorRequired: d.nitErrorRequired || 'Se requiere número de NIT para continuar con su compra.',
      errorFormat: d.nitErrorFormat || 'El NIT solo puede contener números y guiones (ej. 1234-56789-101-1).',
      errorSave: d.nitErrorSave || 'No pudimos guardar su NIT. Revise su conexión e intente de nuevo.',
      dialogTitle: d.nitDialogTitle || 'Consumidor Final',
      dialogDesc: d.nitDialogDesc || '¿Desea continuar su compra como Consumidor Final?',
      dialogConfirm: d.nitDialogConfirm || 'Sí, continuar',
      dialogCancel: d.nitDialogCancel || 'No, ingresar NIT'
    };
  }

  // ─── Error UI ─────────────────────────────────────────────────────────────

  function showError(input, message) {
    var errorEl = document.getElementById(input.id + '-error');
    input.setAttribute('aria-invalid', 'true');
    input.classList.add('cart-nit-field__input--error');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.removeAttribute('hidden');
    }
    input.focus();
  }

  function clearError(input) {
    var errorEl = document.getElementById(input.id + '-error');
    input.removeAttribute('aria-invalid');
    input.classList.remove('cart-nit-field__input--error');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.setAttribute('hidden', '');
    }
  }

  // ─── Sincronización entre los dos campos ──────────────────────────────────

  /* En /cart conviven el input de la página (CartNitPage) y el del drawer
     (CartNitModal). Sin esto, lo que el cliente escribe en uno no existe para
     el otro y se pierde según por dónde entre al checkout. */
  function mirrorValue(source) {
    var all = document.querySelectorAll(FIELD_SELECTOR);
    for (var i = 0; i < all.length; i++) {
      if (all[i] !== source && all[i].value !== source.value) {
        all[i].value = source.value;
        clearError(all[i]);
      }
    }
  }

  // ─── Confirmation dialog ──────────────────────────────────────────────────

  var FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  /**
   * Branded "Consumidor Final" confirmation dialog.
   * Resolves to true (continue as CF) or false (go back and enter NIT).
   * @returns {Promise<boolean>}
   */
  function showConfirmDialog(cfg) {
    return new Promise(function (resolve) {
      var existing = document.getElementById('nit-confirm-dialog');
      if (existing) existing.remove();

      var previousFocus = document.activeElement;

      var overlay = document.createElement('div');
      overlay.id = 'nit-confirm-dialog';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-labelledby', 'nit-dialog-title');
      overlay.setAttribute('aria-describedby', 'nit-dialog-desc');
      overlay.innerHTML = [
        '<div class="nit-dialog__backdrop"></div>',
        '<div class="nit-dialog__box" role="document" tabindex="-1">',
        '  <p id="nit-dialog-title" class="nit-dialog__title"></p>',
        '  <p id="nit-dialog-desc" class="nit-dialog__desc"></p>',
        '  <div class="nit-dialog__actions">',
        '    <button type="button" class="button button-primary nit-dialog__btn--yes" id="nit-dialog-yes"></button>',
        '    <button type="button" class="button button-secondary nit-dialog__btn--no" id="nit-dialog-no"></button>',
        '  </div>',
        '</div>'
      ].join('');

      overlay.querySelector('#nit-dialog-title').textContent = cfg.dialogTitle;
      overlay.querySelector('#nit-dialog-desc').textContent = cfg.dialogDesc;
      overlay.querySelector('#nit-dialog-yes').textContent = cfg.dialogConfirm;
      overlay.querySelector('#nit-dialog-no').textContent = cfg.dialogCancel;

      document.body.appendChild(overlay);

      requestAnimationFrame(function () {
        overlay.classList.add('nit-dialog--open');
      });

      var box = overlay.querySelector('.nit-dialog__box');
      box.focus();

      /* Focus trap: el diálogo es modal, así que Tab no debe salirse al fondo
         (WCAG 2.1.2 "No Keyboard Trap" / 2.4.3 "Focus Order"). El listener va
         en document/capture para seguir funcionando si el foco se escapa. */
      function onKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          close(false);
          return;
        }
        if (event.key !== 'Tab') return;

        var items = Array.prototype.filter.call(
          overlay.querySelectorAll(FOCUSABLE),
          function (el) { return el.offsetParent !== null; }
        );
        if (!items.length) {
          event.preventDefault();
          box.focus();
          return;
        }
        var first = items[0];
        var last = items[items.length - 1];
        var active = document.activeElement;

        if (!overlay.contains(active)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      }
      document.addEventListener('keydown', onKeydown, true);

      var closed = false;
      function close(result) {
        if (closed) return;
        closed = true;
        document.removeEventListener('keydown', onKeydown, true);
        overlay.classList.remove('nit-dialog--open');
        overlay.classList.add('nit-dialog--closing');
        setTimeout(function () {
          overlay.remove();
          /* Devolver el foco a donde estaba; si el flujo sigue, quien llama
             decide moverlo (p. ej. al input cuando el cliente elige "No"). */
          if (previousFocus && document.contains(previousFocus)) previousFocus.focus();
          resolve(result);
        }, 250);
      }

      overlay.querySelector('#nit-dialog-yes').addEventListener('click', function () { close(true); });
      overlay.querySelector('#nit-dialog-no').addEventListener('click', function () { close(false); });
      overlay.querySelector('.nit-dialog__backdrop').addEventListener('click', function () { close(false); });
    });
  }

  // ─── Core validation ──────────────────────────────────────────────────────

  /**
   * Validate the NIT field for the given wrapper.
   * @param {HTMLElement} wrapper - .cart-nit-field element
   * @param {HTMLInputElement} input
   * @returns {Promise<boolean>} true = proceed to checkout, false = block
   */
  function validateNit(wrapper, input) {
    var cfg = getConfig(wrapper);
    var value = normalize(input.value);
    var aboveLimit = cfg.subtotalCents >= cfg.thresholdCents;

    if (aboveLimit) {
      if (value === '' || value === CF_VALUE) {
        showError(input, cfg.errorRequired);
        return Promise.resolve(false);
      }
      if (!isValidNit(value)) {
        showError(input, cfg.errorFormat);
        return Promise.resolve(false);
      }
      clearError(input);
      return Promise.resolve(true);
    }

    if (value === CF_VALUE) {
      clearError(input);
      return Promise.resolve(true);
    }

    if (value !== '' && !isValidNit(value)) {
      showError(input, cfg.errorFormat);
      return Promise.resolve(false);
    }

    if (value !== '') {
      clearError(input);
      return Promise.resolve(true);
    }

    clearError(input);
    return showConfirmDialog(cfg).then(function (confirmed) {
      if (confirmed) {
        input.value = CF_VALUE;
        mirrorValue(input);
        return true;
      }
      input.focus();
      return false;
    });
  }

  // ─── Cart attribute persistence ───────────────────────────────────────────

  /**
   * Persist a NIT value to the cart attribute via the Cart API.
   * Rechaza si el carrito no aceptó el cambio: quien llama decide si bloquea.
   * @param {string} value
   * @returns {Promise<void>}
   */
  function saveNitAttribute(value) {
    var attributes = {};
    attributes[ATTRIBUTE_NAME] = value;
    /* Transitional: drops the pre-rename key from carts opened before this
       deploy. An empty string removes the attribute. Delete once open carts
       have expired. */
    attributes[LEGACY_ATTRIBUTE_NAME] = '';
    return fetch(cartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (response) {
      if (!response.ok) throw new Error('cart/update.js respondió ' + response.status);
    });
  }

  // ─── Checkout submit interception (document-level, capture phase) ────────

  /** Re-lanza el submit original conservando el botón que lo disparó. */
  function resubmit(form, submitter) {
    validatedForms.add(form);
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
      return;
    }
    /* Safari <16: requestSubmit no existe. form.submit() no incluye el
       submitter, así que replicamos su name/value en un hidden. */
    var hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = submitter.name;
    hidden.value = submitter.value || '';
    form.appendChild(hidden);
    form.submit();
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    var submitter = event.submitter;
    var isCheckout = submitter && submitter.name === 'checkout';
    if (!isCheckout) return;

    /* Segundo pase: ya validamos, dejamos que corran el tema y las apps. */
    if (validatedForms.has(form)) {
      validatedForms.delete(form);
      return;
    }

    var wrapper = findNitWrapper(form);
    if (!wrapper) return;

    var input = wrapper.querySelector(FIELD_SELECTOR);
    if (!input) return;

    // Block this pass (and the theme's loading spinner) while validating.
    event.preventDefault();
    event.stopImmediatePropagation();

    validateNit(wrapper, input).then(function (valid) {
      if (!valid) return;

      var nitValue = normalize(input.value);
      input.value = nitValue;
      mirrorValue(input);

      /* El input del modal vive fuera del form de checkout, así que un submit
         nativo no lo incluiría: persistimos antes de re-lanzar. Si el carrito
         rechaza el cambio NO seguimos — antes se navegaba igual y la orden
         llegaba sin NIT. */
      return saveNitAttribute(nitValue).then(
        function () { resubmit(form, submitter); },
        function () { showError(input, getConfig(wrapper).errorSave); }
      );
    });
  }, true);

  // Live: clear the error while typing, and keep both fields in sync
  document.addEventListener('input', function (event) {
    var input = event.target;
    if (input instanceof HTMLInputElement && input.classList.contains('cart-nit-field__input')) {
      clearError(input);
      mirrorValue(input);
    }
  });

  /* Al salir del campo persistimos el valor para que sobreviva a la navegación
     entre el drawer y /cart. Best-effort: si falla, el submit vuelve a
     intentarlo y ahí sí bloquea con un mensaje. */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('cart-nit-field__input')) return;

    mirrorValue(input);
    var value = normalize(input.value);
    if (value === '' || value === CF_VALUE || isValidNit(value)) {
      saveNitAttribute(value).catch(function () {});
    }
  });

  // ─── Clear NIT when the first item is added to an empty cart ─────────────

  document.addEventListener('product:added-to-cart', function () {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.item_count === 1 && cart.attributes && cart.attributes[ATTRIBUTE_NAME]) {
          saveNitAttribute('').catch(function () {});
          document.querySelectorAll(FIELD_SELECTOR).forEach(function (el) {
            el.value = '';
            clearError(el);
          });
        }
      })
      .catch(function () {});
  });
})();
